import React, { useState, useEffect } from 'react';
import { 
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ClockIcon,
  CalendarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import Dialog from '../common/Dialog';
import authService from '../../services/auth';
import tenantUserService from '../../services/tenantUserService';
import tenantService from '../../services/tenantService';

export interface Site {
  id: number;
  name: string;
  code: string;
  tenant?: {
    id: number;
    name: string;
  };
}

export interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active?: boolean;
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  permissions?: any[];
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AssignSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (siteId: number, roleId: string, sessionType: 'PERMANENTE' | 'TEMPORAIRE', isDefault: boolean, startDate?: string, endDate?: string) => Promise<void>;
  sites: Site[];
  roles: Role[];
  userId: number;
  userName: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  loading?: boolean;
}

type SessionType = 'PERMANENTE' | 'TEMPORAIRE';

const AssignSiteModal: React.FC<AssignSiteModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  sites,
  roles,
  userName,
  userEmail,
  userFirstName,
  userLastName,
  loading = false
}) => {
  const [selectedSite, setSelectedSite] = useState<number | ''>('');
  const [selectedRole, setSelectedRole] = useState<string | ''>('');
  const [sessionType, setSessionType] = useState<SessionType>('PERMANENTE');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // États pour le filtrage des sites et rôles
  const [filteredSites, setFilteredSites] = useState<Site[]>(sites);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>(roles);
  
  // États pour la gestion des tenants
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | ''>('');
  const [userRole, setUserRole] = useState<string>('');

  // Vérifier le type d'utilisateur et filtrer les sites et rôles
  const checkUserStatusAndFilterSites = async () => {
    try {
      // Récupérer le profil de l'utilisateur connecté
      const userProfile = await authService.getUserProfile();
      const currentUserRole = userProfile.role?.name || '';
      
      setUserRole(currentUserRole);
      
      const platformRoles = ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'];
      const tenantRoles = ['TENANT_ADMIN', 'TENANT_MANAGER', 'TENANT_OWNER'];
      const siteRoles = ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'];
      
      let filteredSitesList: Site[] = [];
      let filteredRolesList = roles;
      let tenantsList: Tenant[] = [];
      let currentUserTenantId: number | null = null;
      
      if (platformRoles.includes(currentUserRole)) {
        // Utilisateurs PLATFORM : charger tous les tenants et afficher le sélecteur
        try {
          tenantsList = await tenantService.getTenants();
          setTenants(tenantsList);
          setSelectedTenant(''); // Pas de tenant sélectionné par défaut
        } catch (error) {
          console.warn('⚠️ Impossible de récupérer la liste des tenants:', error);
        }
        
        // Filtrer les rôles pour les utilisateurs PLATFORM
        filteredRolesList = roles.filter(role => role.name !== 'SUPERADMIN');
        
      } else if (tenantRoles.includes(currentUserRole)) {
        // Utilisateurs TENANT : récupérer le tenant de l'utilisateur et filtrer les sites
        try {
          const tenantUserInfo = await tenantUserService.getCurrentTenantUser();
          if (tenantUserInfo) {
            currentUserTenantId = tenantUserInfo.tenant_id;
            setSelectedTenant(currentUserTenantId);
            
            // Filtrer les sites par tenant de l'utilisateur
            filteredSitesList = sites.filter(site => {
              const siteTenantId = site.tenant?.id || (site as any).tenant || 0;
              return siteTenantId === currentUserTenantId;
            });
          }
        } catch (error) {
          console.warn('⚠️ Impossible de récupérer les informations tenant user:', error);
        }
        
        // Filtrer les rôles pour les utilisateurs tenant
        filteredRolesList = roles.filter(role => {
          const allowedRoles = ['SITE_SUPERVISOR', 'SITE_MANAGER', 'SITE_ADMIN', 'QUALITY_AGENT', 'OPERATOR', 'MANAGER', 'ADMIN', 'VIEWER'];
          return allowedRoles.includes(role.name);
        });
        
      } else if (siteRoles.includes(currentUserRole)) {
        // Utilisateurs SITE : afficher uniquement les sites assignés
        if (userProfile.sites) {
          const assignedSiteIds = userProfile.sites.map((site: any) => site.id);
          filteredSitesList = sites.filter(site => {
            return assignedSiteIds.includes(site.id);
          });
        }
        
        // Filtrer les rôles pour les utilisateurs site
        filteredRolesList = roles.filter(role => {
          const allowedRoles = ['QUALITY_AGENT', 'OPERATOR', 'MANAGER', 'ADMIN', 'VIEWER'];
          return allowedRoles.includes(role.name);
        });
        
      } else if (currentUserRole === 'ADMIN') {
        // ADMIN : afficher tous les sites
        filteredSitesList = sites;
        
        // ADMIN peut assigner : QUALITY_AGENT, OPERATOR, MANAGER, VIEWER
        filteredRolesList = roles.filter(role => {
          const allowedRoles = ['QUALITY_AGENT', 'OPERATOR', 'MANAGER', 'VIEWER'];
          return allowedRoles.includes(role.name);
        });
        
      } else {
        // Autres utilisateurs système : afficher tous les sites
        filteredSitesList = sites;
        filteredRolesList = roles.filter(role => role.name !== 'SUPERADMIN');
      }
      
      setFilteredSites(filteredSitesList);
      setFilteredRoles(filteredRolesList);
      
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du statut utilisateur:', error);
      setFilteredSites(sites); // Fallback vers tous les sites
      setFilteredRoles(roles.filter(role => role.name !== 'SUPERADMIN')); // Fallback vers tous les rôles sauf SUPERADMIN
    }
  };

  // Filtrer les sites selon le tenant sélectionné (pour les utilisateurs PLATFORM)
  const filterSitesBySelectedTenant = () => {
    if (userRole && ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'].includes(userRole)) {
      if (selectedTenant) {
        const sitesForTenant = sites.filter(site => {
          const siteTenantId = site.tenant?.id || (site as any).tenant || 0;
          return siteTenantId === selectedTenant;
        });
        setFilteredSites(sitesForTenant);
      } else {
        // Si aucun tenant sélectionné, afficher tous les sites
        setFilteredSites(sites);
      }
    }
  };

  // Effet pour filtrer les sites quand le tenant sélectionné change
  useEffect(() => {
    filterSitesBySelectedTenant();
  }, [selectedTenant, userRole, sites]);

  // Réinitialiser les sélections quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setSelectedSite('');
      setSelectedRole('');
      setSessionType('PERMANENTE');
      setStartDate('');
      setEndDate('');
      setIsDefault(false);
      
      // Vérifier le statut utilisateur et filtrer les sites et rôles
      checkUserStatusAndFilterSites();
    }
  }, [isOpen, sites, roles]);

  // Définir la date de début par défaut (aujourd'hui)
  useEffect(() => {
    if (isOpen && sessionType === 'TEMPORAIRE') {
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      
      // Date de fin par défaut (dans 30 jours)
      const endDateDefault = new Date();
      endDateDefault.setDate(endDateDefault.getDate() + 30);
      setEndDate(endDateDefault.toISOString().split('T')[0]);
    }
  }, [isOpen, sessionType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation selon le rôle de l'utilisateur
    const platformRoles = ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'];
    
    if (platformRoles.includes(userRole)) {
      // Pour les utilisateurs PLATFORM, le tenant est obligatoire
      if (!selectedTenant || !selectedSite || !selectedRole) {
        return;
      }
    } else {
      // Pour les autres utilisateurs, seul le site et le rôle sont obligatoires
      if (!selectedSite || !selectedRole) {
        return;
      }
    }

    // Validation des dates pour les sessions temporaires
    if (sessionType === 'TEMPORAIRE') {
      if (!startDate || !endDate) {
        return;
      }
      if (new Date(startDate) >= new Date(endDate)) {
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await onAssign(
        selectedSite, 
        selectedRole, 
        sessionType,
        isDefault,
        sessionType === 'TEMPORAIRE' ? startDate : undefined,
        sessionType === 'TEMPORAIRE' ? endDate : undefined
      );
      onClose();
    } catch (error) {
      // L'erreur est gérée par le composant parent
      console.error('Erreur lors de l\'assignation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = (() => {
    const platformRoles = ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'];
    
    if (platformRoles.includes(userRole)) {
      // Pour les utilisateurs PLATFORM, le tenant est obligatoire
      return selectedTenant && selectedSite && selectedRole && !isSubmitting && 
        (sessionType === 'PERMANENTE' || (sessionType === 'TEMPORAIRE' && startDate && endDate && new Date(startDate) < new Date(endDate)));
    } else {
      // Pour les autres utilisateurs, seul le site et le rôle sont obligatoires
      return selectedSite && selectedRole && !isSubmitting && 
        (sessionType === 'PERMANENTE' || (sessionType === 'TEMPORAIRE' && startDate && endDate && new Date(startDate) < new Date(endDate)));
    }
  })();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Assigner un site"
      size="lg"
    >
      <div className="space-y-6">
        {/* En-tête avec informations utilisateur */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">
                Assignation pour l'utilisateur
              </h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>
                  <span className="font-medium">{userName}</span>
                  {userFirstName && userLastName && (
                    <span> ({userFirstName} {userLastName})</span>
                  )}
                </p>
                {userEmail && (
                  <p className="text-blue-600">{userEmail}</p>
                )}
              </div>
            </div>
          </div>
        </div>

       

        {/* Formulaire d'assignation */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sélection du tenant (pour les utilisateurs PLATFORM) */}
          {userRole && ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'].includes(userRole) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tenant *
              </label>
              <div className="relative">
                <select
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                  disabled={loading}
                >
                  <option value="">Sélectionner un tenant</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.code})
                    </option>
                  ))}
                </select>
                {selectedTenant && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <div className="flex items-center text-sm text-gray-600">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                      <span>
                        Tenant sélectionné : <span className="font-medium">{tenants.find(t => t.id === selectedTenant)?.name}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sélection du site */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site à assigner *
            </label>
            <div className="relative">
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
                disabled={loading}
              >
                <option value="">Sélectionner un site</option>
                {filteredSites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.code})
                  </option>
                ))}
              </select>
              {selectedSite && (
                <div className="mt-2 p-2 bg-gray-50 rounded border">
                  <div className="flex items-center text-sm text-gray-600">
                    <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                    <span>
                      Site sélectionné : <span className="font-medium">{filteredSites.find(s => s.id === selectedSite)?.name}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sélection du rôle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rôle à attribuer *
            </label>
            <div className="relative">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value ? e.target.value : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
                disabled={loading}
              >
                <option value="">Sélectionner un rôle</option>
                {Array.isArray(filteredRoles) && (
                  <>
                    {/* Section Gestion Plateforme */}
                    {filteredRoles.filter(role => ['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPPORT'].includes(role.name)).length > 0 && (
                      <optgroup label="Gestion Plateforme">
                        {filteredRoles
                          .filter(role => ['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPPORT'].includes(role.name))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description ? role.description.split(' - ')[0] : role.name}
                            </option>
                          ))}
                      </optgroup>
                    )}

                    {/* Section Gestion Tenant */}
                    {filteredRoles.filter(role => ['TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER'].includes(role.name)).length > 0 && (
                      <optgroup label="Gestion Tenant">
                        {filteredRoles
                          .filter(role => ['TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER'].includes(role.name))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description ? role.description.split(' - ')[0] : role.name}
                            </option>
                          ))}
                      </optgroup>
                    )}

                    {/* Section Gestion Site */}
                    {filteredRoles.filter(role => ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'].includes(role.name)).length > 0 && (
                      <optgroup label="Gestion Site">
                        {filteredRoles
                          .filter(role => ['SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR'].includes(role.name))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description ? role.description.split(' - ')[0] : role.name}
                            </option>
                          ))}
                      </optgroup>
                    )}

                    {/* Section Opérationnel */}
                    {filteredRoles.filter(role => ['ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER', 'QUALITY_AGENT', 'CONTROLLER', 'SUPERVISOR', 'TECHNICIAN', 'AUDITOR', 'COMMERCIAL', 'FINANCE', 'STOCK', 'STORE_MANAGER'].includes(role.name)).length > 0 && (
                      <optgroup label="Opérationnel">
                        {filteredRoles
                          .filter(role => ['ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER', 'QUALITY_AGENT', 'CONTROLLER', 'SUPERVISOR', 'TECHNICIAN', 'AUDITOR', 'COMMERCIAL', 'FINANCE', 'STOCK', 'STORE_MANAGER'].includes(role.name))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description ? role.description.split(' - ')[0] : role.name}
                            </option>
                          ))}
                      </optgroup>
                    )}

                    {/* Rôles non catégorisés */}
                    {filteredRoles.filter(role => !['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER', 'SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER', 'QUALITY_AGENT', 'CONTROLLER', 'SUPERVISOR', 'TECHNICIAN', 'AUDITOR', 'COMMERCIAL', 'FINANCE', 'STOCK'].includes(role.name)).length > 0 && (
                      <optgroup label="Autres">
                        {filteredRoles
                          .filter(role => !['PLATFORM_SUPER_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_MANAGER', 'SITE_ADMIN', 'SITE_MANAGER', 'SITE_SUPERVISOR', 'STORE_MANAGER', 'ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER', 'QUALITY_AGENT', 'CONTROLLER', 'SUPERVISOR', 'TECHNICIAN', 'AUDITOR', 'COMMERCIAL', 'FINANCE', 'STOCK'].includes(role.name))
                          .map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.description ? role.description.split(' - ')[0] : role.name}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </>
                )}
              </select>
              {selectedRole && (
                <div className="mt-2 p-2 bg-gray-50 rounded border">
                  <div className="flex items-center text-sm text-gray-600">
                    <ShieldCheckIcon className="h-4 w-4 mr-2" />
                    <span>
                      Rôle sélectionné : <span className="font-medium">{filteredRoles.find(r => r.id === selectedRole)?.description?.split(' - ')[0] || 'Aucune description'}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Option site par défaut */}
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="is_default"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded mt-1"
                disabled={loading}
              />
              <div className="ml-3">
                <label htmlFor="is_default" className="flex items-center text-sm font-medium text-orange-800">
                  <StarIcon className="h-4 w-4 mr-2" />
                  Définir comme site par défaut
                </label>
                <p className="text-sm text-orange-700 mt-1">
                  Cet utilisateur sera automatiquement connecté à ce site lors de sa prochaine connexion. 
                  Seul un site peut être défini comme site par défaut par utilisateur.
                </p>
              </div>
            </div>
          </div>

          {/* Type de session */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Type de session *
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sessionType"
                  value="PERMANENTE"
                  checked={sessionType === 'PERMANENTE'}
                  onChange={(e) => setSessionType(e.target.value as SessionType)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <ClockIcon className="h-4 w-4 inline mr-1" />
                  Session permanente
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sessionType"
                  value="TEMPORAIRE"
                  checked={sessionType === 'TEMPORAIRE'}
                  onChange={(e) => setSessionType(e.target.value as SessionType)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Session temporaire
                </span>
              </label>
            </div>
          </div>

          {/* Configuration de la session temporaire */}
          {sessionType === 'TEMPORAIRE' && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h4 className="text-sm font-medium text-yellow-900 mb-3">
                Configuration de la session temporaire
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-yellow-800 mb-1">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-yellow-800 mb-1">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                    required
                  />
                </div>
              </div>
              <div className="mt-3 p-2 bg-yellow-100 rounded border border-yellow-300">
                <p className="text-xs text-yellow-800">
                  <ClockIcon className="h-3 w-3 inline mr-1" />
                  <strong>Fin de session automatique :</strong> La session se terminera automatiquement le {endDate ? new Date(endDate).toLocaleDateString('fr-FR') : '...'} à 23:59
                </p>
              </div>
            </div>
          )}

          {/* Résumé de l'assignation */}
          {(() => {
            const platformRoles = ['PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN', 'PLATFORM_SUPPORT'];
            const showSummary = platformRoles.includes(userRole) 
              ? selectedTenant && selectedSite && selectedRole 
              : selectedSite && selectedRole;
            
            return showSummary && (
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <h4 className="text-sm font-medium text-orange-900 mb-3">
                  Résumé de l'assignation
                </h4>
                <div className="space-y-2 text-sm text-orange-800">
                  {/* Information du tenant pour les utilisateurs PLATFORM */}
                  {platformRoles.includes(userRole) && selectedTenant && (
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                      <span>
                        <strong>Tenant :</strong> {tenants.find(t => t.id === selectedTenant)?.name} ({tenants.find(t => t.id === selectedTenant)?.code})
                      </span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                    <span>
                      <strong>Site :</strong> {filteredSites.find(s => s.id === selectedSite)?.name} ({filteredSites.find(s => s.id === selectedSite)?.code})
                    </span>
                  </div>
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-4 w-4 mr-2" />
                  <span>
                    <strong>Rôle :</strong> {filteredRoles.find(r => r.id === selectedRole)?.description?.split(' - ')[0] || 'Aucune description'}
                  </span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  <span>
                    <strong>Type de session :</strong> {sessionType === 'PERMANENTE' ? 'Permanente' : 'Temporaire'}
                  </span>
                </div>
                <div className="flex items-center">
                  <StarIcon className="h-4 w-4 mr-2" />
                  <span>
                    <strong>Site par défaut :</strong> {isDefault ? 'Oui' : 'Non'}
                  </span>
                </div>
                {sessionType === 'TEMPORAIRE' && startDate && endDate && (
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    <span>
                      <strong>Période :</strong> Du {new Date(startDate).toLocaleDateString('fr-FR')} au {new Date(endDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                )}
                <div className="flex items-center">
                  <span className="text-xs">
                    L'utilisateur <strong>{userName}</strong> aura accès au site avec les permissions du rôle sélectionné.
                    {sessionType === 'TEMPORAIRE' && ' La session se terminera automatiquement à la date de fin.'}
                  </span>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Assignation...
                </div>
              ) : (
                'Assigner le site'
              )}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AssignSiteModal;
